<template>
  <article
    ref="cardRef"
    class="card-surface stagger-card overflow-hidden rounded-2xl hover:-translate-y-1 hover:border-cyan-100/25 hover:shadow-[0_8px_40px_rgba(0,24,64,0.32)]"
    :data-visible="revealed ? 'true' : 'false'"
    :style="{ transitionDelay: `${delay}ms` }"
  >
    <div :class="coverClasses">
      <a
        v-if="product.coverType === 'image' && product.officialUrl"
        :href="product.officialUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="absolute inset-0 z-[2] block"
        :aria-label="`${displayName} official website`"
      >
        <img
          :src="product.coverUrl"
          :alt="`${displayName} preview image`"
          class="absolute inset-0 h-full w-full object-cover transition-transform duration-300 hover:scale-[1.02]"
          loading="lazy"
        />
      </a>
      <img
        v-else-if="product.coverType === 'image'"
        :src="product.coverUrl"
        :alt="`${displayName} preview image`"
        class="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      <div v-else-if="product.id === 'pagepilot'" class="absolute inset-0 p-4">
        <div class="overflow-hidden rounded-xl border border-white/10 bg-[#071729]/90 shadow-[0_10px_40px_rgba(0,0,0,0.24)]">
          <div class="flex items-center gap-2 border-b border-white/[0.08] px-3 py-2.5">
            <span class="h-2 w-2 rounded-full bg-rose-400"></span>
            <span class="h-2 w-2 rounded-full bg-amber-300"></span>
            <span class="h-2 w-2 rounded-full bg-emerald-400"></span>
            <span class="ml-2 text-[10px] text-slate-300/65">pagepilot.ai/current-page</span>
          </div>
          <div class="grid gap-3 p-3 md:grid-cols-[1.1fr_0.9fr]">
            <div class="rounded-lg border border-white/[0.08] bg-white/[0.04] p-3">
              <div class="h-2.5 w-1/3 rounded-full bg-cyan-200/20"></div>
              <div class="mt-3 space-y-2">
                <div class="h-2 rounded-full bg-white/10"></div>
                <div class="h-2 w-5/6 rounded-full bg-white/10"></div>
                <div class="h-2 w-4/6 rounded-full bg-white/10"></div>
              </div>
            </div>
            <div class="space-y-2">
              <div class="rounded-lg border border-cyan-200/15 bg-cyan-300/[0.12] px-3 py-2 text-xs text-cyan-50">Ask this page</div>
              <div class="rounded-lg border border-white/[0.08] bg-white/[0.05] px-3 py-2 text-xs text-white/90">Summarize article</div>
              <div class="rounded-lg border border-white/[0.08] bg-white/[0.05] px-3 py-2 text-xs text-white/90">Explain selection</div>
            </div>
          </div>
        </div>
      </div>
      <div class="absolute inset-0 bg-gradient-to-t from-[#061120]/58 via-transparent to-transparent"></div>
      <span
        class="absolute right-4 top-4 z-[3] rounded-full border border-white/10 bg-black/50 px-3 py-1 text-xs text-[#f1f1f3] backdrop-blur-sm"
      >
        {{ t(product.badge) }}
      </span>
    </div>

    <div class="p-5">
      <h3 class="text-base font-semibold text-white">{{ displayName }}</h3>
      <p v-if="product.nameZh" class="mt-0.5 text-xs text-[#8b8b99]">{{ product.nameZh }}</p>
      <p class="mt-2 text-sm" :style="{ color: product.accentColor }">{{ t(product.tagline) }}</p>
      <p class="mt-3 text-sm leading-6 text-[#8b8b99]">{{ t(product.desc) }}</p>
      <div v-if="features.length" class="mt-4 flex flex-wrap gap-2">
        <span
          v-for="feature in features"
          :key="feature"
          class="rounded-full border border-cyan-100/10 bg-cyan-300/[0.08] px-2.5 py-1 text-[11px] text-cyan-50/90"
        >
          {{ feature }}
        </span>
      </div>
      <div v-if="product.modelHighlights?.length" class="mt-4 flex flex-wrap gap-2">
        <span
        v-for="model in product.modelHighlights"
        :key="model"
        class="rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-1 text-[11px] text-white/[0.88]"
      >
        {{ model }}
        </span>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 px-5 pb-5">
      <a
        v-for="link in product.links"
        :key="link.url"
        :href="link.url"
        target="_blank"
        rel="noopener noreferrer"
        :class="[
          'rounded-md px-4 py-2 text-sm transition-colors duration-200',
          link.primary
            ? 'border border-white/10 bg-white/10 text-white hover:bg-white/15'
            : 'text-[#8b8b99] hover:text-white',
        ]"
      >
        {{ t(link.label) }}
      </a>
    </div>
  </article>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from '../composables/useI18n'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  delay: {
    type: Number,
    default: 0,
  },
})

const cardRef = ref(null)
const revealed = ref(false)
let observer
const { t, ta } = useI18n()
const displayName = computed(() => t(props.product.name))
const features = computed(() => ta(props.product.features))

const coverClasses = computed(() => {
  const base = 'relative h-[180px] overflow-hidden'

  if (props.product.coverType === 'image') {
    return `${base} bg-[#111118]`
  }

  return `${base} bg-gradient-to-br ${props.product.coverGradient}`
})

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        revealed.value = true
        observer?.disconnect()
      })
    },
    { threshold: 0.2 },
  )

  if (cardRef.value) {
    observer.observe(cardRef.value)
  }
})

onUnmounted(() => {
  observer?.disconnect()
})
</script>
